import {
  ARCHETYPES,
  ARCHETYPES_OPTIONS,
  CURRENCY_NAMES,
  ENGAGEMENT_TYPES,
  ENGAGEMENT_TYPES_OPTIONS,
  MINIMUM_TARGET_RATE,
  WORK_ARRANGEMENT_OPTIONS,
  WORK_ARRANGEMENTS,
} from "constants/index";
import { markdownToSlate, stripMarkdown } from "./markdownToSlate";

const DEFAULT_TIMEZONE = "America_New_York"; // UTC-5, matches MissionRole default
const DEFAULT_ENGAGEMENT_TIME = 40; // 40 hours/week for full-time roles
const DEFAULT_DAYS_TO_START = 14; // Default start date is 2 weeks from now

/**
 * Extracts job title from description's first line if title is missing
 * @param {string|null} title - The job title from AI results
 * @param {string} description - The job description from AI results
 * @returns {{ name: string, description: string }} - Extracted/cleaned name and description
 */
export const extractJobTitleFromDescription = (title, description) => {
  let name = title;
  let cleanedDescription = description;

  // If no title, extract first line from description
  if (!name?.trim() && description) {
    const lines = description
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length > 0) {
      // Extract and strip markdown from first line
      name = stripMarkdown(lines[0]);

      // Remove first line from description if there are more lines
      if (lines.length > 1) {
        cleanedDescription = lines.slice(1).join("\n");
      }
    }
  } else if (name) {
    // Strip markdown from provided title too
    name = stripMarkdown(name);
  }

  return {
    name: name?.trim() || "",
    description: cleanedDescription?.trim() || "",
  };
};

/**
 * Converts array of skill strings to tag format for GraphQL mutation
 * @param {Array<string>} items - Array of skill/tag names
 * @returns {Array<{name: string}>} - Array of tag objects
 */
export const toTagFormat = (items) => {
  if (!Array.isArray(items)) return [];

  return items.map((item) => {
    if (typeof item === "string") {
      return { name: item, id: item }; // id is required to make field work well
    }

    return item; // Already in correct format
  });
};

/**
 * Transforms and prepares AI role results for mission role creation
 *
 * This is a convenience function that combines transformAIRoleResults and applyAIRoleDefaults
 * to reduce boilerplate in components.
 *
 * @param {Object} results - AI role builder results
 * @param {Object} options - Additional context for defaults
 * @param {string} options.userId - Current user ID for assignedTo (sales owner)
 * @returns {Object|null} - Complete role data with defaults, or null if invalid
 *
 * @example
 * const roleData = prepareAIRoleData(pendingJob.results, { userId: auth.id });
 */
export const prepareAIRoleData = (results, options = {}) => {
  const transformedData = transformAIRoleResults(results);
  if (!transformedData) return null;

  return applyAIRoleDefaults(transformedData, options);
};

/**
 * Transforms AI role results to mission role format
 * @param {Object} results - AI role builder results
 * @returns {Object|null} - Formatted role data or null if invalid
 */
const transformAIRoleResults = (results) => {
  const { missionRole } = results || {};

  if (!missionRole) return null;

  // Extract job title and description with fallback logic
  const { name, description } = extractJobTitleFromDescription(missionRole.jobTitle, missionRole.jobDescription);
  const descriptionJson = markdownToSlate(description); // Convert markdown description to Slate JSON format
  const archetype = ARCHETYPES_OPTIONS.filter((item) => missionRole.archetypes.includes(item.name)).map(({ id }) => id); // Map archetype names to their corresponding IDs based on ARCHETYPES_OPTIONS constant
  const engagementType = ENGAGEMENT_TYPES_OPTIONS.find(({ label }) => label === missionRole.engagementType)?.value; // Map engagement type label to its corresponding value based on ENGAGEMENT_TYPES_OPTIONS constant
  const workArrangement = WORK_ARRANGEMENT_OPTIONS.find(({ label }) => label === missionRole.workArrangement)?.value; // Map work arrangement label to its corresponding value based on WORK_ARRANGEMENTS constant

  return {
    name,
    description, // Keep plain text for backwards compatibility
    descriptionJson, // Add Slate JSON format for editor
    archetype,
    engagementType,
    workArrangement,
    disciplines: toTagFormat(missionRole.disciplines),
    primarySkills: toTagFormat(missionRole.primarySkills),
    secondarySkills: toTagFormat(missionRole.secondarySkills),
    otherSkills: toTagFormat(missionRole.otherSkills),
  };
};

/**
 * Applies sensible defaults to AI-generated role data
 *
 * This utility ensures AI-generated roles have all required fields
 * with appropriate default values that match business requirements
 * and model validations.
 *
 * @param {Object} aiRoleData - Transformed AI role data from transformAIRoleResults
 * @param {Object} options - Additional context for defaults
 * @param {string} options.userId - Current user ID for assignedTo (sales owner)
 * @returns {Object} - Role data with defaults applied
 *
 * @example
 * const roleData = transformAIRoleResults(aiResults);
 * const roleWithDefaults = applyAIRoleDefaults(roleData, { userId: auth.id });
 */
const applyAIRoleDefaults = (aiRoleData, options = {}) => {
  const { userId } = options;

  // Calculate start date: 2 week from now
  const startDate = new Date();
  startDate.setDate(startDate.getDate() + DEFAULT_DAYS_TO_START);
  const formattedStartDate = startDate.toISOString().split("T")[0]; // YYYY-MM-DD format

  return {
    ...aiRoleData,

    // Archetype: contributor (matches ARCHETYPES.contributor.id)
    archetype: aiRoleData.archetype || ARCHETYPES.contributor.id,

    // Start date: 1 week from now
    startDate: aiRoleData.startDate || formattedStartDate,

    // Engagement time: 40 hours default (full-time)
    engagementTime: aiRoleData.engagementTime || DEFAULT_ENGAGEMENT_TIME,

    // Engagement type: full_time_employment (matches model enum)
    engagementType: aiRoleData.engagementType || ENGAGEMENT_TYPES.full_time_employment,

    // Work arrangement: remote (matches model enum)
    workArrangement: aiRoleData.workArrangement || WORK_ARRANGEMENTS.remote,

    // Target rate: Enforce minimum target rate
    targetRate: aiRoleData.targetRate || MINIMUM_TARGET_RATE,

    // Currency: CAD (matches model enum)
    targetRateCurrency: aiRoleData.targetRateCurrency || CURRENCY_NAMES.cad,

    // Timezone: America/New_York (UTC-5)
    timezone: { value: aiRoleData.timezone || DEFAULT_TIMEZONE },

    // Sales owner: Current admin user
    assignedTo: { id: aiRoleData.assignedToId || userId },

    // Always create as unpublished (allows review before publishing)
    isVisible: false,
  };
};
